import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { SavedView } from './ViewsPane'

/**
 * What the screen on view wants listed in the pane down the left.
 *
 * `Shell.tsx` has passed `useMemo<SavedView[]>(() => [], [])` to `ViewsPane` since the redesign
 * landed - a hardcoded empty list, because there was no way for a screen to say anything. The pane
 * printed "No saved views yet" on every route and meant "nobody built this yet" rather than anything
 * about the tenant.
 *
 * The counts were a separate and deliberate hold. `ViewsPane`'s own comment says they were withheld
 * under D-263 until an API could answer them: "rendering a plausible number next to a saved query on a
 * security console is exactly the failure D-263 exists to prevent, so the count arrives when the API
 * can answer it." The ways-in endpoint answers it - five counts, fleet-wide, in the same round trip as
 * the page - so the hold lifts for this screen and stays for every screen that still cannot (D-450).
 *
 * A registry rather than a prop, for the same reason the shortcuts one is: the shell must not know
 * which screens have views, and a screen that unmounts must take its views with it rather than leave
 * them advertising a filter that no longer exists.
 */
interface Registry {
  views: readonly SavedView[]
  publish: (views: readonly SavedView[]) => () => void
}

const SavedViewsContext = createContext<Registry>({ views: [], publish: () => () => {} })

export function SavedViewsProvider({ children }: { children: React.ReactNode }) {
  const [views, setViews] = useState<readonly SavedView[]>([])

  const value = useMemo<Registry>(
    () => ({
      views,
      publish: (next: readonly SavedView[]) => {
        setViews(next)
        return () => setViews([])
      }
    }),
    [views]
  )

  return <SavedViewsContext.Provider value={value}>{children}</SavedViewsContext.Provider>
}

/** What the shell hands to the pane. */
export function useSavedViewRegistry(): Registry {
  return useContext(SavedViewsContext)
}

/**
 * Lists this screen's views for as long as it is mounted.
 *
 * Callers memoize, because the registry re-publishes on identity change and a fresh array each render
 * would loop.
 */
export function useSavedViews(views: readonly SavedView[]): void {
  const { publish } = useSavedViewRegistry()
  useEffect(() => publish(views), [publish, views])
}
